import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import GridContainer from '@/components/Grid/GridContainer';
import GridItem from '@/components/Grid/GridItem';
import { TextField, Modal, Box } from '@mui/material';
import { useStyles } from '@/styles/templateInputStyles';
import Button from '@/components/CustomButtons/Button';
import axios from 'axios';
import { API_BASE_URL } from '@/config';
import { Document, Page } from 'react-pdf';

export default function EconsentTemplateForm({
  formInputs,
  onInputChange,
  labelStyle,
  disabled = false,
  logoData,
}) {
  const { t } = useTranslation();
  const classes = useStyles();
  const [pdfPreview, setPdfPreview] = useState(null);
  const [isPdfPreviewOpen, setIsPdfPreviewOpen] = useState(false);
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);

  const closePdfPreview = () => {
    setIsPdfPreviewOpen(false);
  };

  function onDocumentLoadSuccess({ numPages }) {
    setNumPages(numPages);
    setPageNumber(1);
  }

  const onPreview = async (e) => {
    try {
      e.preventDefault();
      const formData = new FormData();

      formData.append('body', formInputs.body || '');

      // Set the correct logo data based on use_business_unit_logo flag
      if (logoData.use_business_unit_logo) {
        // Don't append any logo file if using business unit logo
        formData.append('use_business_unit_logo', true);
      } else if (logoData.logo) {
        formData.append('logo', logoData.logo);
      }

      formData.append('include_cpd_logo', logoData.include_cpd_logo || false);
      formData.append(
        'use_business_unit_logo',
        logoData.use_business_unit_logo || false,
      );

      formData.append('name', formInputs.name || '');
      formData.append('date', formInputs.date || '');
      formData.append('signature', formInputs.signature || '');

      const response = await axios.post(
        API_BASE_URL + '/audiences/preview-letter',
        formData,
        { responseType: 'blob' },
      );

      const blob = new Blob([response.data], {
        type: response.headers['content-type'] || 'application/pdf',
      });

      const reader = new FileReader();
      reader.onload = (e) => {
        setPdfPreview(e.target.result);
        setIsPdfPreviewOpen(true);
      };
      reader.readAsDataURL(blob);
    } catch (err) {
      console.log(err);
    }
  };

  return (
    <form>
      <GridContainer>
        <GridItem xs={12} sm={12} md={6}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              padding: '5px',
            }}>
            <label style={labelStyle}>{t('letterTemplate.fields.name')}</label>
            <TextField
              variant='outlined'
              className={classes.root}
              multiline
              inputProps={{
                style: {
                  fontSize: '14px',
                  lineHeight: '1.3',
                  width: '100%',
                },
              }}
              minRows={1}
              maxRows={2}
              name='name'
              value={formInputs?.name ?? ''}
              onChange={onInputChange}
              disabled={disabled}
            />
          </div>
        </GridItem>
        <GridItem xs={12} sm={12} md={6}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              padding: '5px',
            }}>
            <label style={labelStyle}>{t('letterTemplate.fields.date')}</label>
            <TextField
              variant='outlined'
              className={classes.root}
              multiline
              inputProps={{
                style: {
                  fontSize: '14px',
                  lineHeight: '1.3',
                  width: '100%',
                },
              }}
              minRows={1}
              maxRows={2}
              name='date'
              value={formInputs?.date ?? ''}
              onChange={onInputChange}
              disabled={disabled}
            />
          </div>
        </GridItem>
        <GridItem xs={12} sm={12} md={12}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              padding: '5px',
            }}>
            <label style={labelStyle}>{t('letterTemplate.fields.body')}</label>
            <TextField
              variant='outlined'
              className={classes.root}
              multiline
              inputProps={{
                style: {
                  fontSize: '14px',
                  lineHeight: '1.3',
                  width: '100%',
                },
              }}
              minRows={9}
              maxRows={12}
              name='body'
              value={formInputs?.body ?? ''}
              onChange={onInputChange}
              disabled={disabled}
            />
          </div>
        </GridItem>
        <GridItem xs={12} sm={12} md={6}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              padding: '5px',
            }}>
            <label style={labelStyle}>
              {t('letterTemplate.fields.signature')}
            </label>
            <TextField
              variant='outlined'
              className={classes.root}
              multiline
              inputProps={{
                style: {
                  fontSize: '14px',
                  lineHeight: '1.3',
                  width: '100%',
                },
              }}
              minRows={3}
              maxRows={3}
              name='signature'
              value={formInputs?.signature ?? ''}
              onChange={onInputChange}
              disabled={disabled}
            />
          </div>
        </GridItem>
      </GridContainer>

      {!disabled && (
        <div style={{ marginTop: '20px' }}>
          <Button
            variant='contained'
            color='primary'
            onClick={onPreview}
            type='button'>
            {t('letterTemplate.buttons.preview')}
          </Button>
        </div>
      )}

      <Modal
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        open={isPdfPreviewOpen && pdfPreview !== null}
        onClose={closePdfPreview}>
        <Box
          style={{
            height: '90vh',
            background: '#fff',
            borderRadius: '8px',
            width: '90vw',
            maxWidth: '800px',
            padding: '20px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}>
          <div
            style={{
              flex: 1,
              overflow: 'auto',
              textAlign: 'center',
              border: '1px solid #ccc',
            }}>
            <Document
              file={pdfPreview}
              onLoadSuccess={onDocumentLoadSuccess}
              loading={<div>{t('letterTemplate.preview.loading')}</div>}
              error={<div>{t('letterTemplate.preview.error')}</div>}>
              <Page pageNumber={pageNumber} />
            </Document>
          </div>

          <div style={{ padding: '10px', textAlign: 'center' }}>
            <p>
              {t('letterTemplate.preview.pageInfo', {
                current: pageNumber,
                total: numPages,
              })}
            </p>

            <div>
              <Button color='primary' onClick={closePdfPreview}>
                {t('letterTemplate.buttons.close')}
              </Button>
            </div>
          </div>
        </Box>
      </Modal>
    </form>
  );
}
